import {
  Button,
  Container,
  createListCollection,
  DatePicker,
  Flex,
  Grid,
  GridItem,
  parseDate,
  Portal,
  Select,
  Separator,
  Span,
  Spinner,
  Text
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { type ChangeEvent, useEffect, useState } from "react"
import SearchField from "../../components/Search/SearchField"
import { formatDate, getSearchResults } from "../../api/search"
import { PublicPublicationsOrderBy } from "../../client"
import { FiCalendar } from "react-icons/fi"
import TagsField from "../../components/Search/TagsField"
import PublicationSearchPreview from "../../components/Search/PublicationSearchPreview"
import NotFound from "../../components/Common/NotFound"

export const Route = createFileRoute("/_layout/suche")({
  component: Search,
  notFoundComponent: NotFound,
})


function Search() {
  const searchParams = useSearch({ from: "/_layout/suche" })
  const navigate = useNavigate()

  const [visibleItemCount, setVisibleItemCount] = useState(0)
  const [searchValue, setSearchValue] = useState<string>("")
  const [orderBy, setOrderBy] = useState<PublicPublicationsOrderBy>("publication_dt")
  const [isDesc, setIsDesc] = useState<boolean>(true)
  const [dateFrom, setDateFrom] = useState<string | null>(null)
  const [dateTo, setDateTo] = useState<string | null>(null)
  const [tagId, setTagId] = useState<string | null>(null)
  const [tagName, setTagName] = useState<string | null>(null)

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value)
  }

  const {
    data: publications,
    isFetching,
    refetch,
  } = useQuery({
     ...getSearchResults({
      authorId: null,
      categoryId: null,
      dateFrom: dateFrom,
      dateTo: dateTo,
      descending: isDesc,
      orderBy: orderBy,
      query: searchValue,
      page: visibleItemCount,
      tagId: tagId,
    }),
    enabled: false,
    placeholderData: (prevData) => prevData,
  })

  const showMore = publications?.count !== publications?.data?.length

  const sortOptions = createListCollection({
    items: [
      { label: "Publikationsdatum absteigend", value: "publication_dt:desc" },
      { label: "Publikationsdatum aufsteigend", value: "publication_dt:asc" },
      { label: "Titel absteigend", value: "title:desc" },
      { label: "Titel aufsteigend", value: "title:asc" },
      { label: "ID absteigend", value: "classic_id:desc"},
      { label: "ID aufsteigend", value: "classic_id:asc"},
    ],
  })

  const resetAll = () => {
    setSearchValue("")
    setOrderBy("publication_dt")
    setIsDesc(true)
    setDateFrom(null)
    setDateTo(null)
    setTagId(null)
    setTagName(null)
    setVisibleItemCount(0)

    navigate({ search: undefined })

    refetch()
  }

  useEffect(() => {
    if (searchParams.tagId && searchParams.tagName) {
      setTagId(searchParams.tagId)
      setTagName(searchParams.tagName)
    }
  }, [searchParams])


  useEffect(() => {
    refetch()
  }, [orderBy, isDesc, tagId, tagName])

  return (
    <Container display="flex" flexDir="column">
      <Flex flexDirection="column">
        <SearchField
          inputValue={searchValue}
          onInputChange={handleInputChange}
        />

        <Flex my={{ base: 5, md: 10 }} flexDirection={{ base: "column", md: "row" }}>
          {/* From date */}
          <DatePicker.Root
            locale="de-CH"
            mr={{base: 0, md: 5}}
            mb={{base: 5, md: 0}}
            onValueChange={(e) => setDateFrom(formatDate(e.value[0]))}
            value={dateFrom ? [parseDate(dateFrom)] : []}
          >
            <DatePicker.Label fontSize={16}>Von</DatePicker.Label>
            <DatePicker.Control>
              <DatePicker.Input />
              <DatePicker.IndicatorGroup>
                <DatePicker.Trigger>
                  <FiCalendar />
                </DatePicker.Trigger>
              </DatePicker.IndicatorGroup>
            </DatePicker.Control>
            <Portal>
              <DatePicker.Positioner>
                <DatePicker.Content>
                  <DatePicker.View view="day">
                    <DatePicker.Header />
                    <DatePicker.DayTable />
                  </DatePicker.View>
                  <DatePicker.View view="month">
                    <DatePicker.Header />
                    <DatePicker.MonthTable />
                  </DatePicker.View>
                  <DatePicker.View view="year">
                    <DatePicker.Header />
                    <DatePicker.YearTable />
                  </DatePicker.View>
                </DatePicker.Content>
              </DatePicker.Positioner>
            </Portal>
          </DatePicker.Root>

          {/* To date */}
          <DatePicker.Root
            locale="de-CH"
            mx={{base: 0, md: 5}}
            mb={{base: 5, md: 0}}
            onValueChange={(e) => setDateTo(formatDate(e.value[0]))}
            value={dateTo ? [parseDate(dateTo)] : []}
          >
            <DatePicker.Label fontSize={16}>Bis</DatePicker.Label>
            <DatePicker.Control>
              <DatePicker.Input />
              <DatePicker.IndicatorGroup>
                <DatePicker.Trigger>
                  <FiCalendar />
                </DatePicker.Trigger>
              </DatePicker.IndicatorGroup>
            </DatePicker.Control>
            <Portal>
              <DatePicker.Positioner>
                <DatePicker.Content>
                  <DatePicker.View view="day">
                    <DatePicker.Header />
                    <DatePicker.DayTable />
                  </DatePicker.View>
                  <DatePicker.View view="month">
                    <DatePicker.Header />
                    <DatePicker.MonthTable />
                  </DatePicker.View>
                  <DatePicker.View view="year">
                    <DatePicker.Header />
                    <DatePicker.YearTable />
                  </DatePicker.View>
                </DatePicker.Content>
              </DatePicker.Positioner>
            </Portal>
          </DatePicker.Root>

          {/* Tags */}
          <TagsField
            value={tagName}
            onChange={(id, name) => {
              setTagId(id)
              setTagName(name)
            }}
          />
        </Flex>

        <Flex justifyContent="space-between" flexDirection={{base: "column", md: "row"}} >
          <Select.Root
            collection={sortOptions}
            my={{ base: 10, md: 0 }}
            size="sm"
            w={{base: "full", md: "50%"}}
            value={[`${orderBy}:${isDesc ? "desc" : "asc"}`]}
            onValueChange={(e) => {
              const [field, dir] = e.value[0].split(":")
              setOrderBy(field as PublicPublicationsOrderBy)
              setIsDesc(dir === "desc")
            }}
          >
            <Select.HiddenSelect />

            <Flex align="center" gap={5}>
              <Select.Label whiteSpace="nowrap" fontSize={16}>Sortieren nach: </Select.Label>

              <Select.Control>
                <Select.Trigger
                  unstyled
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  gap={5}
                  whiteSpace="nowrap"
                >
                  <Select.ValueText maxW="fit-content" />
                </Select.Trigger>
                <Select.IndicatorGroup paddingInline="unset" right={-5}>
                  <Select.Indicator />
                </Select.IndicatorGroup>
              </Select.Control>
            </Flex>
            <Portal>
              <Select.Positioner w="fit-content">
                <Select.Content w={{base: "max-content", md: "20em"}}>
                  {sortOptions.items.map((val) => (
                    <Select.Item item={val} key={val.value}>
                      {val.label}
                      <Select.ItemIndicator />
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Positioner>
            </Portal>
          </Select.Root>

          <Flex gap={5}>
            <Button
              variant="primary"
              textTransform="uppercase"
              onClick={() => refetch()}
            >
              Suchen
            </Button>

            <Button
              variant="danger"
              textTransform="uppercase"
              onClick={resetAll}
            >
              Zurücksetzen
            </Button>
          </Flex>
        </Flex>
      </Flex>

      <Separator my={10} borderColor="main" size="md" />

      <Flex flexDirection="column">
        {isFetching ? (
          <Flex mt={10} justifyContent="center" alignItems="center">
            <Spinner size="lg" color="main" />
          </Flex>
        ) : (
            publications?.data && publications?.data?.length > 0 ? (
              <>
                <Text fontSize={16}><Span fontWeight="bold">{publications?.data?.length}</Span> Suchergebnisse gefunden</Text>
                <Grid my={10}>
                  {publications?.data?.map((pub, i) => (
                    <GridItem key={pub.id} my={5}>
                      <PublicationSearchPreview publication={pub} />
                      {(publications.data.length -1) != i &&
                        <Separator my={10} borderColor="main" size="md" />
                      }
                    </GridItem>
                  ))}
                </Grid>
              </>
            ) : (
              <Flex mt={10} justifyContent="center">
                <Text color="danger" fontSize={18} textAlign="center">Es konnten keine Suchergebnisse gefunden werden</Text>
              </Flex>
            )
        )}
      </Flex>

      {showMore && (
        <Flex justifyContent="center">
            <Button
                onClick={() => setVisibleItemCount(visibleItemCount + 100)}
                variant="secondary"
                textTransform="uppercase"
                mt="10"
                w="fit-content"
            >
                Mehr Beiträge laden
            </Button>
        </Flex>
      )}
    </Container>
  )
}
